// ==========================
// Version 12 — auth/Register.js
// - Sends verification email for email/password registration
// - Redirects new email/password users to "/verify-email"
// - Requires adult confirmation and versioned legal acceptance for email and Google registration
// ==========================
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  deleteUser,
  getAdditionalUserInfo,
  sendEmailVerification,
  signOut,
  signInWithPopup,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import Seo from '../components/Seo';
import { getEmailVerificationActionSettings } from './emailVerification';
import {
  hasRequiredRegistrationConsent,
  recordRegistrationConsent,
} from './registrationConsent';
import './Register.css';

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const IVORY = '#FDFCF9';
const WARM_GRAY = '#888888';
const BURGUNDY = '#5C1A2B';

function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const validateConsent = () => {
    if (hasRequiredRegistrationConsent({ ageConfirmed, legalAccepted })) {
      return true;
    }

    setError(
      'Confirm that you are at least 18 and accept the Terms and Privacy Notice to create an account.'
    );
    return false;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateConsent()) return;

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setError('Password should be at least 6 characters.');
      return;
    }

    try {
      setBusy(true);
      const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      try {
        await recordRegistrationConsent(credential.user, 'email');
      } catch (consentError) {
        await deleteUser(credential.user).catch(() => signOut(auth));
        throw consentError;
      }
      await sendEmailVerification(credential.user, getEmailVerificationActionSettings());
      navigate('/verify-email', { state: { email: credential.user.email, sent: true } });
    } catch (err) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleGoogleRegister = async () => {
    setError('');
    if (!validateConsent()) return;

    try {
      setBusy(true);
      const credential = await signInWithPopup(auth, googleProvider);
      try {
        await recordRegistrationConsent(credential.user, 'google');
      } catch (consentError) {
        if (getAdditionalUserInfo(credential)?.isNewUser) {
          await deleteUser(credential.user).catch(() => signOut(auth));
        } else {
          await signOut(auth).catch(() => {});
        }
        throw consentError;
      }
      navigate('/home');
    } catch (err) {
      setError(err?.message || 'Google sign-in failed.');
    } finally {
      setBusy(false);
    }
  };

  const pageUrl = typeof window !== 'undefined' ? `${window.location.origin}/register` : undefined;

  return (
    <div className="register-page" style={styles.page}>
      <Seo
        title="Create account • Blessed with a Kiss"
        description="Join BWAK and start immortalizing your memories on the map."
        url={pageUrl}
        type="website"
      />

      <div style={styles.glowTL} />
      <div style={styles.glowBR} />

      <div className="register-card" style={styles.card} aria-label="Create account">
        <h2 style={styles.title}>Create Account</h2>
        <p style={styles.subtitle}>Join and start immortalizing your memories.</p>

        {error ? <div style={styles.error} role="alert">{error}</div> : null}

        <form onSubmit={handleRegister} style={styles.form}>
          <label style={styles.label} htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={styles.input}
            placeholder="you@example.com"
            disabled={busy}
          />

          <label style={styles.label} htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={styles.input}
            placeholder="••••••••"
            disabled={busy}
          />

          <label style={styles.label} htmlFor="confirm">Confirm Password</label>
          <input
            id="confirm"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            style={styles.input}
            placeholder="••••••••"
            disabled={busy}
          />

          <fieldset style={styles.consentGroup} aria-describedby="registration-consent-help">
            <legend style={styles.consentLegend}>Before you create an account</legend>
            <p id="registration-consent-help" style={styles.consentHelp}>
              These confirmations are required for email and Google registration.
            </p>

            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
                disabled={busy}
                style={styles.checkbox}
              />
              <span>I confirm that I am at least 18 years old.</span>
            </label>

            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={legalAccepted}
                onChange={(e) => setLegalAccepted(e.target.checked)}
                disabled={busy}
                style={styles.checkbox}
              />
              <span>
                I agree to the{' '}
                <Link to="/terms" target="_blank" rel="noreferrer" style={styles.legalLink}>
                  Terms of Use
                </Link>
                {' '}and acknowledge the{' '}
                <Link to="/privacy" target="_blank" rel="noreferrer" style={styles.legalLink}>
                  Privacy Notice
                </Link>.
              </span>
            </label>
          </fieldset>

          <button type="submit" style={styles.primaryBtn} disabled={busy}>
            {busy ? 'Creating account…' : 'Register'}
          </button>
        </form>

        <div style={styles.dividerWrap} aria-hidden="true">
          <div style={styles.dividerLine} />
          <span style={styles.dividerText}>or</span>
          <div style={styles.dividerLine} />
        </div>

        <button onClick={handleGoogleRegister} style={styles.googleBtn} disabled={busy}>
          <img
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            alt=""
            style={styles.googleIcon}
          />
          Continue with Google
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: {
    width: '100%',
    boxSizing: 'border-box',
    minHeight: 'calc(100dvh - 64px)',
    background: NAVY,
    display: 'grid',
    placeItems: 'center',
    padding: 16,
    position: 'relative',
    overflow: 'hidden',
  },
  glowTL: {
    position: 'absolute',
    top: -120,
    left: -120,
    width: 280,
    height: 280,
    borderRadius: '50%',
    filter: 'blur(40px)',
    opacity: 0.25,
    background: GOLD,
  },
  glowBR: {
    position: 'absolute',
    bottom: -140,
    right: -140,
    width: 340,
    height: 340,
    borderRadius: '50%',
    filter: 'blur(50px)',
    opacity: 0.2,
    background: GOLD,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    minWidth: 0,
    boxSizing: 'border-box',
    background: IVORY,
    borderRadius: 20,
    padding: 'var(--register-card-padding)',
    boxShadow: '0 24px 64px rgba(10,26,51,0.40)',
    border: `1px solid rgba(201,164,76,0.35)`,
    color: NAVY,
  },
  title: {
    margin: 0,
    fontFamily: 'serif',
    fontSize: 26,
  },
  subtitle: {
    margin: '6px 0 var(--register-subtitle-bottom)',
    color: WARM_GRAY,
  },
  error: {
    margin: '8px 0 12px',
    background: 'rgba(92,26,43,0.08)',
    color: BURGUNDY,
    border: '1px solid rgba(92,26,43,0.28)',
    padding: '8px 10px',
    borderRadius: 10,
    fontSize: 14,
  },
  form: {
    display: 'grid',
    gap: 'var(--register-form-gap)',
  },
  label: {
    fontWeight: 700,
    marginTop: 'var(--register-label-margin)',
  },
  input: {
    padding: 12,
    borderRadius: 12,
    border: `1px solid rgba(10,26,51,0.15)`,
    background: '#fff',
    color: NAVY,
    outline: 'none',
    fontSize: 14,
  },
  consentGroup: {
    minWidth: 0,
    margin: 'var(--register-consent-margin) 0 0',
    padding: 'var(--register-consent-padding)',
    borderRadius: 12,
    border: '1px solid rgba(10,26,51,0.18)',
    background: 'rgba(201,164,76,0.08)',
    display: 'grid',
    gap: 'var(--register-consent-gap)',
  },
  consentLegend: {
    padding: '0 4px',
    color: NAVY,
    fontWeight: 800,
    fontSize: 14,
  },
  consentHelp: {
    margin: 0,
    color: '#5B6472',
    fontSize: 13,
    lineHeight: 1.45,
  },
  checkboxLabel: {
    minHeight: 44,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: 'var(--register-checkbox-padding) 6px',
    color: NAVY,
    fontSize: 14,
    lineHeight: 1.45,
    cursor: 'pointer',
  },
  checkbox: {
    width: 20,
    height: 20,
    margin: '1px 0 0',
    flex: '0 0 auto',
    accentColor: GOLD,
  },
  legalLink: {
    color: '#765817',
    fontWeight: 700,
    textDecorationThickness: '1px',
    textUnderlineOffset: '2px',
  },
  primaryBtn: {
    marginTop: 'var(--register-button-margin)',
    padding: '12px 14px',
    borderRadius: 12,
    border: 'none',
    background: GOLD,
    color: NAVY,
    cursor: 'pointer',
    fontWeight: 800,
    fontSize: 15,
  },
  dividerWrap: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    gap: 10,
    margin: 'var(--register-divider-margin) 0',
  },
  dividerLine: {
    height: 1,
    background: 'rgba(10,26,51,0.15)',
  },
  dividerText: {
    color: WARM_GRAY,
    fontWeight: 700,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  googleBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 12,
    width: '100%',
    background: '#fff',
    border: `1px solid rgba(10,26,51,0.15)`,
    borderRadius: 12,
    cursor: 'pointer',
    fontWeight: 800,
    fontSize: 14,
    color: NAVY,
  },
  googleIcon: {
    width: 20,
    height: 20,
  },
};

export default Register;
// ==========================
// End Version 12 — auth/Register.js
// ==========================
